import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  ADMIN_GUIDE_SECTIONS,
  ILLUSTRATED_GUIDE_URL,
  illustratedGuideUrl,
  type GuideSection,
} from "../content/adminGuide";

interface AdminGuidePageProps {
  /** true en /guia (compartible/imprimible): sin links internos del admin. */
  publicMode?: boolean;
}

function screenshotUrl(section: GuideSection): string {
  return `${import.meta.env.BASE_URL}guia/${section.screenshot}`;
}

/** Guía rápida interactiva (Eduardo 2026-08-23): una pestaña por sección,
 * con la captura real de la pantalla + pasos cortos + "Ojo con…". Misma
 * fuente que el botón "?" del dock. */
export default function AdminGuidePage({ publicMode = false }: AdminGuidePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get("seccion");
  const tournamentParam = searchParams.get("tournament");
  const tournamentId = tournamentParam && /^\d+$/.test(tournamentParam) ? tournamentParam : null;
  const [brokenShots, setBrokenShots] = useState<Record<string, boolean>>({});

  const active = useMemo(
    () => ADMIN_GUIDE_SECTIONS.find((section) => section.key === sectionParam) ?? ADMIN_GUIDE_SECTIONS[0],
    [sectionParam],
  );

  function selectSection(key: string) {
    const next = new URLSearchParams(searchParams);
    next.set("seccion", key);
    setSearchParams(next, { replace: true });
  }

  const goPath = active.tournamentPath && tournamentId
    ? `/admin/tournaments/${tournamentId}${active.tournamentPath}`
    : active.tournamentPath
      ? "/admin/tournaments"
      : "/admin/payments";

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <p className="eyebrow">Torneos123 · material de apoyo</p>
            <h1 className="card__title">Guía rápida del administrador</h1>
            <p className="card__subtitle">
              El orden de un torneo: Datos → Reglamento → Convocatoria → Inscripciones →
              Jugadores → Draw → Calendario → Resultados. Elige una sección para ver su
              pantalla y sus pasos.
            </p>
          </div>
          <a
            className="btn"
            href={ILLUSTRATED_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
          >
            📸 Guía ilustrada completa
          </a>
        </div>
        <nav className="guide-tabs" aria-label="Secciones de la guía">
          {ADMIN_GUIDE_SECTIONS.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`guide-tab${section.key === active.key ? " is-active" : ""}`}
              onClick={() => selectSection(section.key)}
            >
              <span aria-hidden="true">{section.icon}</span> {section.order}. {section.title}
            </button>
          ))}
        </nav>
      </section>

      <section className="card guide-detail">
        <div className="guide-detail__head">
          <div>
            <p className="eyebrow">Paso {active.order} de {ADMIN_GUIDE_SECTIONS.length}</p>
            <h2 className="card__title">{active.icon} {active.title}</h2>
            <p className="card__subtitle">{active.purpose}</p>
          </div>
          {!publicMode && (
            <Link className="btn" to={goPath}>Ir a esta sección →</Link>
          )}
        </div>

        <div className="guide-detail__grid">
          <figure className="guide-shot">
            {brokenShots[active.key] ? (
              /* Sin captura local: la guía ilustrada (78 capturas embebidas)
                 cubre la sección — el marco deja de ser un "pendiente". */
              <a className="guide-shot__placeholder guide-shot__illustrated" href={illustratedGuideUrl(active)} target="_blank" rel="noreferrer">
                <strong>📸 Ver esta sección con capturas</strong>
                <small>Se abre la guía ilustrada en {active.illustratedAnchor ? "el apartado correspondiente" : "su inicio"}.</small>
              </a>
            ) : (
              <img
                src={screenshotUrl(active)}
                alt={`Pantalla de ${active.title}`}
                onError={() => setBrokenShots((current) => ({ ...current, [active.key]: true }))}
              />
            )}
            <figcaption className="muted">
              Así se ve la sección {active.title} en Torneos123.{" "}
              <a href={illustratedGuideUrl(active)} target="_blank" rel="noreferrer">
                Ver con capturas paso a paso →
              </a>
            </figcaption>
          </figure>

          <div className="guide-steps">
            <h3>Qué haces aquí</h3>
            <ol>
              {active.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            {active.warnings.length > 0 && (
              <>
                <h3>Ojo con…</h3>
                <ul className="guide-warnings">
                  {active.warnings.map((warning) => (
                    <li key={warning}>⚠️ {warning}</li>
                  ))}
                </ul>
              </>
            )}
            <p className="guide-done">✅ <strong>Listo cuando…</strong> {active.doneWhen}</p>
          </div>
        </div>

        <div className="guide-detail__nav">
          {active.order > 1 && (
            <button type="button" className="btn btn--ghost" onClick={() => selectSection(ADMIN_GUIDE_SECTIONS[active.order - 2].key)}>
              ← {ADMIN_GUIDE_SECTIONS[active.order - 2].title}
            </button>
          )}
          {active.order < ADMIN_GUIDE_SECTIONS.length && (
            <button type="button" className="btn" onClick={() => selectSection(ADMIN_GUIDE_SECTIONS[active.order].key)}>
              Siguiente: {ADMIN_GUIDE_SECTIONS[active.order].title} →
            </button>
          )}
        </div>
      </section>
    </>
  );
}
